import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  inject,
  signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ModalComponent, ModalConfig } from '../modal/modal';
import { IconComponent } from '../icon/icon';
import { UploadRowComponent } from '../upload-row/upload-row';
import {
  OrgManagementApi,
  UploadAndExtractScope,
  UploadFailureReason,
  UploadLifecycleEvent,
} from '../../services/org-management-api/org-management-api';
import { ContextDocument, ContextDocumentOwnership } from '../../models';
import {
  CONTEXT_DOC_ACCEPT_ATTRIBUTE,
  CONTEXT_DOC_MAX_SIZE_BYTES,
  CONTEXT_DOC_SUPPORTED_FORMATS_LABEL,
  isAllowedContextDocFile,
} from '../../constants/context-document.constants';
import { denialOrFallback } from '../../utils/access-error.utils';
import type { UploadRowStatus } from '../../utils/status-indicator.utils';

/** Subset of the shared upload-row vocabulary this surface uses. The modal
 *  drives every file through the full lifecycle exposed by the service —
 *  `pending → uploading → extracting → ready/failed` — so the user sees
 *  the same set of phases here as in the import-assistant queue. */
type ModalRowStatus = Extract<
  UploadRowStatus,
  'pending' | 'uploading' | 'extracting' | 'ready' | 'failed'
>;

interface UploadRow {
  id: string;
  file: File;
  status: ModalRowStatus;
  error: string | null;
  /** True when {@link error} is a permission answer rather than a failure.
   *  Swaps the row from red-with-a-cross to amber-with-a-lock, and drops
   *  the retry affordance — retrying a refusal cannot succeed. */
  errorIsDenial: boolean;
  doc: ContextDocument | null;
}

/** Bounded parallelism — small files upload quickly, but we don't fan out
 *  infinitely. The cap applies to the upload phase; extraction polling
 *  for already-uploaded rows runs in parallel on top. */
const MAX_CONCURRENCY = 3;

@Component({
  selector: 'app-context-document-upload-modal',
  templateUrl: './context-document-upload-modal.html',
  styleUrl: './context-document-upload-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ModalComponent, IconComponent, UploadRowComponent, TranslocoModule],
})
export class ContextDocumentUploadModal {
  private readonly orgManagementApi = inject(OrgManagementApi);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly translationVersion = signal(0);

  constructor() {
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.translationVersion.update(v => v + 1));
  }

  readonly isVisible = input.required<boolean>();
  readonly orgId = input.required<string>();
  /** Owning org for the uploaded doc — passed as `customerOrgId` in the
   *  upload scope so the doc lands in the correct DDB partition. Only
   *  needed when a manager uploads on behalf of a customer org (where
   *  `orgId` is the manager org used for the API path). */
  readonly customerOrgId = input<string | null>(null);
  /** Defaults to the visible organization library. Entity flows override
   * this with their source owner so attachments never silently become shared. */
  readonly ownership = input<ContextDocumentOwnership>({ usage_scope: 'organization' });
  readonly orgName = input<string | null>(null);

  /** Emitted after the user closes the modal — carries the successfully uploaded docs. */
  readonly closed = output<ContextDocument[]>();
  readonly cancelled = output<void>();

  protected readonly rows = signal<UploadRow[]>([]);
  protected readonly isDragOver = signal(false);

  protected readonly acceptAttribute = CONTEXT_DOC_ACCEPT_ATTRIBUTE;
  protected readonly supportedFormatsLabel = CONTEXT_DOC_SUPPORTED_FORMATS_LABEL;
  protected readonly scopeHintKey = computed(() => {
    switch (this.ownership().usage_scope) {
      case 'organization':
        return 'ctx_upload_modal.scope_organization';
      case 'assessment':
        return 'ctx_upload_modal.scope_assessment';
      case 'import':
        return 'ctx_upload_modal.scope_import';
      case 'document':
        return 'ctx_upload_modal.scope_document';
      case 'session':
        return 'ctx_upload_modal.scope_session';
      case 'country_research':
        return 'ctx_upload_modal.scope_country_research';
      case 'unclassified':
        return 'ctx_upload_modal.scope_unclassified';
      default:
        return 'ctx_upload_modal.scope_unclassified';
    }
  });

  protected readonly hasRows = computed(() => this.rows().length > 0);
  protected readonly successCount = computed(
    () => this.rows().filter(r => r.status === 'ready').length
  );
  protected readonly errorCount = computed(
    () => this.rows().filter(r => r.status === 'failed').length
  );
  /** True while ANY row is still being uploaded or extracted. Used to
   *  drive the modal's primary-button label/spinner so the user can't
   *  treat "Done" as terminal while extraction is still running in
   *  crp-context. */
  protected readonly isBusy = computed(() =>
    this.rows().some(
      r => r.status === 'pending' || r.status === 'uploading' || r.status === 'extracting'
    )
  );

  protected readonly modalConfig = computed<ModalConfig>(() => {
    this.translationVersion();
    return {
      title: this.transloco.translate('ctx_upload_modal.modal_title'),
      size: 'large',
      showCloseButton: true,
      showFooter: true,
      primaryButtonText: this.transloco.translate(
        this.isBusy()
          ? 'ctx_upload_modal.modal_primary_uploading'
          : 'ctx_upload_modal.modal_primary_done'
      ),
      primaryButtonLoading: this.isBusy(),
      secondaryButtonText: this.transloco.translate('common.cancel'),
    };
  });

  /**
   * One Subscription per in-flight row, keyed by `UploadRow.id`. Wraps the
   * service's `uploadAndExtractContextDocument` stream which carries the
   * row from upload through extraction polling to a terminal `ready` /
   * `failed`. `removeRow` and the destroy path drain this map so a
   * cancelled row never resurfaces.
   */
  private readonly rowSubs = new Map<string, Subscription>();

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.queueFiles(Array.from(input.files));
      input.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const target = event.target as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (!relatedTarget || !target.contains(relatedTarget)) {
      this.isDragOver.set(false);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
    const files = event.dataTransfer?.files;
    if (files?.length) this.queueFiles(Array.from(files));
  }

  onConfirm(): void {
    // "Done" — emit successful uploads.
    this.emitClosed();
  }

  onCancel(): void {
    // Cancel — we still surface any successful uploads so the parent can
    // refresh its list (the user can't "undo" an upload that already
    // completed). Pending / in-flight rows have their subscriptions
    // cancelled via `emitClosed`.
    this.emitClosed();
    this.cancelled.emit();
  }

  retryRow(row: UploadRow): void {
    if (row.status !== 'failed') return;
    // Cancel any leftover subscription (shouldn't exist, but defensive).
    this.rowSubs.get(row.id)?.unsubscribe();
    this.rowSubs.delete(row.id);
    this.updateRow(row.id, { status: 'pending', error: null, errorIsDenial: false, doc: null });
    this.processQueue();
  }

  removeRow(row: UploadRow): void {
    if (row.status === 'uploading') return;
    // Cancel any in-flight extraction polling for this row before
    // dropping it — otherwise the stream would still patch the row's
    // signal slot after we've evicted it from the list.
    this.rowSubs.get(row.id)?.unsubscribe();
    this.rowSubs.delete(row.id);
    this.rows.update(list => list.filter(r => r.id !== row.id));
  }

  trackRow(_: number, row: UploadRow): string {
    return row.id;
  }

  private queueFiles(files: File[]): void {
    const newRows: UploadRow[] = files.map(file => {
      const validationError = this.validate(file);
      return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        status: validationError ? 'failed' : 'pending',
        error: validationError,
        // Client-side validation (type / size) — never a permission answer.
        errorIsDenial: false,
        doc: null,
      };
    });
    this.rows.update(list => [...list, ...newRows]);
    this.processQueue();
  }

  private validate(file: File): string | null {
    if (file.size > CONTEXT_DOC_MAX_SIZE_BYTES)
      return this.transloco.translate('ctx_upload_modal.validation_size');
    if (!isAllowedContextDocFile(file))
      return this.transloco.translate('ctx_upload_modal.validation_unsupported');
    return null;
  }

  /** Drive up to MAX_CONCURRENCY rows through the upload phase at a time.
   *  Extraction polling continues for already-uploaded rows in the
   *  background — only the upload (S3 PUT) is gated by the concurrency
   *  cap, since that's where we don't want to flood the browser with
   *  concurrent multi-MB PUTs. */
  private processQueue(): void {
    const inFlightUploads = this.rows().filter(r => r.status === 'uploading').length;
    if (inFlightUploads >= MAX_CONCURRENCY) return;

    const slots = MAX_CONCURRENCY - inFlightUploads;
    const next = this.rows()
      .filter(r => r.status === 'pending')
      .slice(0, slots);

    for (const row of next) this.startRow(row);
  }

  /** Subscribe one row to the service's lifecycle stream.
   *
   *  The modal historically called `uploadContextDocument` directly and
   *  stopped the row at `ready` once the S3 PUT landed — semantically
   *  lying, because extraction was still running async in crp-context.
   *  This now flows the row through `uploading → extracting → ready`
   *  faithfully via the same primitive the import-assistant uses, so
   *  both surfaces agree on what "ready" actually means. */
  private startRow(row: UploadRow): void {
    // Pre-emptively cancel any stale subscription for the same row id
    // (retry path) so we never have two concurrent lifecycles racing
    // patches on the same row.
    this.rowSubs.get(row.id)?.unsubscribe();
    const custOrg = this.customerOrgId();
    const scope: UploadAndExtractScope = {
      callerOrgId: this.orgId(),
      ...(custOrg ? { customerOrgId: custOrg } : {}),
      ownership: this.ownership(),
    };

    const sub = this.orgManagementApi
      .uploadAndExtractContextDocument(scope, row.file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: event => this.applyEvent(row.id, event),
        complete: () => {
          this.rowSubs.delete(row.id);
          this.processQueue();
        },
        error: err => {
          // Service contract: failures emit a structured `failed` event.
          // Defence-in-depth — if anything DOES bubble up here we surface
          // it as `upload_failed`.
          this.applyEvent(row.id, {
            phase: 'failed',
            reason: 'upload_failed',
            message: err instanceof Error ? err.message : undefined,
          });
          this.rowSubs.delete(row.id);
          this.processQueue();
        },
      });
    this.rowSubs.set(row.id, sub);
  }

  /** Reduce one lifecycle event into the row's signal state. Single
   *  funnel keeps the event-to-state mapping in one place. */
  private applyEvent(rowId: string, event: UploadLifecycleEvent): void {
    switch (event.phase) {
      case 'uploading':
        this.updateRow(rowId, { status: 'uploading', error: null, errorIsDenial: false });
        return;
      case 'extracting':
        this.updateRow(rowId, {
          status: 'extracting',
          doc: event.doc,
          error: null,
          errorIsDenial: false,
        });
        return;
      case 'ready':
        this.updateRow(rowId, {
          status: 'ready',
          doc: event.doc,
          error: null,
          errorIsDenial: false,
        });
        return;
      case 'failed': {
        // `event.error` is present only for request failures. A 403 there
        // becomes the localised "view-only access to …" sentence; every
        // other failure keeps the reason-derived copy.
        const { message, isDenial } = denialOrFallback(
          event.error,
          this.failureMessage(event.reason, event.message),
          this.transloco
        );
        this.updateRow(rowId, {
          status: 'failed',
          doc: event.doc ?? null,
          error: message,
          errorIsDenial: isDenial,
        });
        return;
      }
    }
  }

  /** Map the structured failure reason to user-visible copy. The modal
   *  shares the import-assistant's i18n keys where they're conceptually
   *  identical — single source of truth for "extraction timed out" etc.
   *
   *  The `never` arm forces a compile error if `UploadFailureReason`
   *  gains a variant without a corresponding case here. */
  private failureMessage(reason: UploadFailureReason, underlying?: string): string {
    if ((reason === 'upload_failed' || reason === 'poll_error') && underlying) {
      return underlying;
    }
    const key: string = (() => {
      switch (reason) {
        case 'upload_failed':
          return 'ctx_upload_modal.upload_failed';
        case 'extraction_failed':
          return 'import_assistant.extraction_failed';
        case 'extraction_no_text':
          return 'import_assistant.extraction_no_text';
        case 'extraction_timeout':
          return 'import_assistant.extraction_timed_out';
        case 'doc_removed':
        case 'poll_error':
          return 'import_assistant.extraction_check_failed';
        default: {
          const _exhaustive: never = reason;
          return _exhaustive;
        }
      }
    })();
    return this.transloco.translate(key);
  }

  private updateRow(id: string, patch: Partial<UploadRow>): void {
    this.rows.update(list => list.map(r => (r.id === id ? { ...r, ...patch } : r)));
  }

  private emitClosed(): void {
    // Cancel any in-flight subscriptions — the modal is going away, so
    // the streams must stop patching state we no longer own. Any S3 PUT
    // already in flight continues to completion; crp-context will still
    // extract the doc and it'll surface in the library on next refresh.
    for (const sub of this.rowSubs.values()) sub.unsubscribe();
    this.rowSubs.clear();
    const uploaded = this.rows()
      .filter(r => r.status === 'ready' && r.doc)
      .map(r => r.doc as ContextDocument);
    this.closed.emit(uploaded);
    this.rows.set([]);
  }
}
